import { useState } from "react";
import { Link } from "react-router-dom";
import { Cog, FileText, Plus, StickyNote, Trash2 } from "lucide-react";
import { Button, Input, Textarea } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

const fmtDateTime = (d: string | Date) =>
  new Date(d).toLocaleString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Auftragsjournal — lückenloser Verlauf: Statuswechsel und Ereignisse der verknüpften Dokumente
 * (Angebot versendet/angenommen, Rechnung angelegt/ausgestellt/bezahlt …) entstehen automatisch,
 * dazu freie Notizen. Jeder Eintrag zeigt Datum, Uhrzeit und wer es ausgelöst hat; Einträge mit
 * Dokumentbezug sind direkt anklickbar.
 */
export function OrderJournalSection({ orderId }: { orderId: string }) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const entries = trpc.order.journal.useQuery({ orderId });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const refresh = () => utils.order.journal.invalidate({ orderId });

  const add = trpc.order.addJournalNote.useMutation({
    onSuccess: () => {
      setTitle("");
      setBody("");
      refresh();
      addToast({ type: "success", title: "Notiz hinzugefügt" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const del = trpc.order.deleteJournalNote.useMutation({ onSuccess: refresh });

  return (
    <section>
      <h2 className="text-sm font-semibold text-text-primary mb-3">
        Auftragsjournal
      </h2>

      <div className="card p-4 mb-4">
        <Input
          label="Notiz (Betreff)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="mt-3">
          <Textarea
            label="Details (optional)"
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            variant="primary"
            size="sm"
            loading={add.isPending}
            disabled={!title.trim()}
            onClick={() =>
              add.mutate({
                orderId,
                title: title.trim(),
                body: body.trim() || null,
              })
            }
          >
            <Plus className="w-3.5 h-3.5" /> Notiz
          </Button>
        </div>
      </div>

      {entries.isLoading ? (
        <p className="text-sm text-text-tertiary">Lädt …</p>
      ) : !entries.data?.length ? (
        <p className="text-sm text-text-tertiary">Noch keine Einträge.</p>
      ) : (
        <ol className="space-y-2">
          {entries.data.map((e) => {
            const Icon = e.type === "note" ? StickyNote : Cog;
            const href =
              e.refType === "quote"
                ? `/angebote/${e.refId}`
                : e.refType === "invoice"
                  ? `/rechnungen/${e.refId}`
                  : null;
            return (
              <li key={e.id} className="flex gap-3">
                <div className="w-7 h-7 rounded-md bg-bg-active flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-3.5 h-3.5 text-text-tertiary" />
                </div>
                <div className="flex-1 min-w-0 card p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary">
                        {e.title}
                      </p>
                      {href && (
                        <Link
                          to={href}
                          className="inline-flex items-center gap-1 text-xs text-accent hover:underline mt-0.5"
                        >
                          <FileText className="w-3 h-3" />
                          {e.refType === "quote"
                            ? "Angebot öffnen"
                            : "Rechnung öffnen"}
                        </Link>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-text-tertiary text-right">
                        {fmtDateTime(e.occurredAt as unknown as string)}
                        {e.actorName ? ` · ${e.actorName}` : ""}
                      </span>
                      {e.type === "note" && (
                        <button
                          className="text-text-tertiary hover:text-danger"
                          onClick={() => del.mutate({ id: e.id, orderId })}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  {e.body && (
                    <p className="text-sm text-text-secondary mt-1 whitespace-pre-wrap">
                      {e.body}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
